import {mapGetters} from 'vuex'
import {
  passwordPolicyDisplayRules,
  requiresServerValidation
} from 'js/lib/password-policy'
import PasswordPolicyModal from 'js/components/password-policy-modal'
import PasswordInput from 'js/components/password-input'
import template from './_login-form.pug'
import navigationMixin from '../../mixins/navigation'
import passengerAuthFormValidation from '../../mixins/passenger-auth-form-validation'

export default {
  name: 'login-form-partial',
  mixins: [navigationMixin, passengerAuthFormValidation],
  template: template(),
  components: {
    PasswordPolicyModal,
    PasswordInput
  },
  props: {
    step: {
      type: String,
      default: 'login'
    }
  },
  data() {
    return {
      web_title: Vue.store.state.webTitle,
      steps: ['login', 'resetPassword', 'resetPasswordFinish', 'register', 'registerFinish', 'confirmEmail'],
      localStep: 'login',
      loginEmail: '',
      loginPassword: '',
      loginError: undefined,
      resetPasswordEmail: '',
      resetPasswordError: undefined,
      registerEmail: '',
      registerPassword: '',
      registerPasswordConfirmation: '',
      registerAcceptConditions: false,
      registerAcceptConditionsError: undefined,
      registrationError: undefined,
      keyCloakError: '',
      registerErrors: {
        email: undefined,
        password: undefined,
        password_confirmation: undefined
      },
      confirmationEmail: '',
      confirmationStatus: 'idle',
      confirmationError: undefined,
      resendConfirmationError: undefined,
      resendConfirmationLoading: false,
      fonts: ["cursive"],
      captchaValue: '',
      inputCaptcha: '',
      inputCaptchaError: undefined,
      shouldShowConditions: false,
      useCaptcha: false,
      showPasswordPolicyModal: false
    }
  },
  computed: {
    ...mapGetters('appConfig', ['effectiveRegistrationPath', 'getPasswordPolicy']),
    registeredText() {
      return this.$t('loginForm.registerFinish.textHelp3')
    },
    ...mapGetters('municipality', ['availableMunicipalities']),
    needsCaptcha() {
      return this.$store.getters.localhost !== 'true'
    },
    loginUserErrorClass() {
      if (this.loginError) {
        return 'error'
      }
      return undefined
    },
    getLoginPanelTitle() {
      return this.$t(`loginForm.${this.localStep}.title`, { web_title: this.web_title })
    },
    acceptTermsVoice() {
      return !this.registerAcceptConditions ? this.$t('loginForm.register.helptextConditionsCheckboxSelect') : this.$t('loginForm.register.helptextConditionsCheckboxDeselect')
    },
    successResetPassword() {
      return this.$t('loginForm.resetPassword.textHelp1')
    },
    passwordPolicyRules() {
      return passwordPolicyDisplayRules(this.getPasswordPolicy)
    },
    passwordPolicyRequiresServerValidation() {
      return requiresServerValidation(this.getPasswordPolicy)
    },
    shouldShowPasswordPolicyHint() {
      return this.passwordPolicyRules.length > 0 || this.passwordPolicyRequiresServerValidation
    }
  },
  watch: {
    step(newStep) {
      if (this.localStep !== newStep) {
        this.localStep = newStep
      }
    },
    localStep(newStep) {
      this.$emit('change-step', newStep)
      this.loginError = undefined
      this.resetPasswordEmail = ''
      this.resetPasswordError = undefined
      // No reseteamos captcha al cambiar de paso porque puede romper el flujo de login/registro
      // (el usuario escribe el captcha pero captchaValue cambia a '').
      this.inputCaptchaError = undefined
      this.confirmationError = undefined
      this.resendConfirmationError = undefined
      this.confirmationStatus = newStep === 'confirmEmail' ? 'loading' : 'idle'

      // Limpia el HTML del captcha para evitar que quede visible en el paso confirmEmail.
      // El captcha se pinta vía DOM en `.preview` (setcaptcha()).
      if (newStep === 'confirmEmail') {
        const previewEl = document?.querySelector?.('.preview')
        if (previewEl) previewEl.innerHTML = ''

        // En confirmEmail no usamos captcha: limpiamos la entrada para que no se "cuele" visualmente.
        this.inputCaptcha = ''
      }

      // Si entramos en la pantalla de confirmación y hay token en URL, confirmamos automáticamente.
      if (newStep === 'confirmEmail') {
        const token =
          this.$route?.query?.confirmation_token ||
          this.$route?.query?.token ||
          this.$route?.params?.token ||
          this.confirmationToken
        if (token) {
          this.confirmEmail()
        } else {
          this.confirmationStatus = 'unconfirmed'
        }
      }
    }
  },
  created() {
    this.localStep = this.step

    // Permite que la página web de confirmación redirija a home abriendo el panel de login.
    if (this.$route?.query?.openLogin) {
      this.localStep = 'login'
    }
  },
  mounted() {
    this.gencaptcha()
    this.setcaptcha()
  },
  methods: {

    openPasswordPolicyModal() {
      this.showPasswordPolicyModal = true
    },

    closePasswordPolicyModal() {
      this.showPasswordPolicyModal = false
    },

    login() {
      this.inputCaptchaError = undefined
      this.touchAllLoginValidationFields()

      if (!this.validateLoginForm() || this.hasLoginBackendValidationErrors) return

      if (this.loginEmail) {
        this.loginEmail = $.trim(this.loginEmail)
      }

      this.loginError = undefined

      if (this.needsCaptcha && this.inputCaptcha !== this.captchaValue) {
        this.inputCaptchaError = this.$t('contact.captcha')
      } else {
        this.$auth.login({
          email: this.loginEmail,
          password: this.loginPassword
        }).then(
          this.handleLoginSuccess,
          this.handleLoginError,
        )
      }
    },

    register() {
      this.touchAllRegistrationValidationFields()

      if (!this.validateRegistrationForm() ||
        this.hasRegistrationBackendValidationErrors) return

      if (this.registerEmail) {
        this.registerEmail = $.trim(this.registerEmail)
        this.registerPassword = $.trim(this.registerPassword)
        this.registerPasswordConfirmation = $.trim(this.registerPasswordConfirmation)
      }

      this.registerErrors = {
        email: undefined,
        password: undefined,
        password_confirmation: undefined
      }
      this.registerAcceptConditionsError = undefined
      this.registrationError = undefined
      this.keyCloakError = ''

      if (this.needsCaptcha && this.inputCaptcha !== this.captchaValue) {
        this.inputCaptchaError = this.$t('contact.captcha')
      } else {
        this.$auth.registration({
          email: this.registerEmail,
          password: this.registerPassword,
          password_confirmation: this.registerPasswordConfirmation
        }).then(
          this.handleRegisterSuccess,
          this.handleRegisterError
        )
      }
    },

    resetPassword() {
      if (this.resetPasswordEmail) {
        this.resetPasswordEmail = $.trim(this.resetPasswordEmail)
      }

      this.resetPasswordError = undefined
      if (this.needsCaptcha && this.inputCaptcha !== this.captchaValue) {
        this.inputCaptchaError = this.$t('contact.captcha')
      } else {
        this.$auth.resetPassword({
          email: this.resetPasswordEmail
        }).then(response => {
          /** Handle displaying the error on the screen */
          if (response.hasOwnProperty('errors')) {
            this.resetPasswordError = response.errors.email
          } else {
            this.handleResetPasswordSuccess()
          }
        })
      }
    },

    handleLoginSuccess(response) {
      this.$auth.setToken(response.data.data.jwt)
      Vue.store.dispatch('loadCurrentUser').then(valid => {
        if (valid) {
          Vue.store.dispatch('user/getAllClientData')
          this.$emit('close-panel')
        } else {
          this.loginError = this.$t('errors.invalidUser')
        }
      })
    },

    handleLoginError(response) {
      if ([0, 504].includes(response.status)) {
        this.$emit('connection-error')
        return
      }

      if (response.status === 422) {
        const sessionError = response.data?.errors?.session
        const sessionErrorCode =
          response.data?.errorCodes?.session ||
          (Array.isArray(sessionError) ? sessionError?.[0]?.error : undefined)

        if (sessionErrorCode === 'unconfirmed') {
          this.$auth.removeToken()
          this.confirmationEmail = this.loginEmail
          this.confirmationStatus = 'unconfirmed'
          this.loginError = this.$t('loginForm.login.unconfirmed')
          this.localStep = 'confirmEmail'

          return
        }

        this.loginError = sessionError
        this.hasLoginBackendValidationErrors = true
      }
    },

    handleResetPasswordSuccess() {
      this.localStep = 'resetPasswordFinish'
    },

    handleRegisterSuccess(response) {
      const registrationPath = response?.registration_path
      const registrationStatus = response?.registration_status
      const hasJwt = Object.prototype.hasOwnProperty.call(response || {}, 'jwt')
      const hasValidJwt = hasJwt && typeof response.jwt === 'string' && response.jwt.length > 0
      const isLegacyRegistration =
        this.effectiveRegistrationPath === 'legacy' &&
        registrationPath === 'legacy' &&
        registrationStatus === 'active' &&
        hasValidJwt
      const isPendingRegistration =
        this.effectiveRegistrationPath === 'email_verification' &&
        registrationPath === 'email_verification' &&
        registrationStatus === 'pending_confirmation' &&
        !hasJwt

      this.registrationError = undefined

      if (isLegacyRegistration) {
        this.$auth.setToken(response.jwt)
        Vue.store.dispatch('loadCurrentUser')
        this.$emit('register-finish')
        this.localStep = 'registerFinish'
        return
      }

      if (isPendingRegistration) {
        this.$auth.removeToken()
        this.confirmationEmail = response.email || this.registerEmail
        this.confirmationStatus = 'unconfirmed'
        this.$emit('register-finish')
        this.localStep = 'confirmEmail'
        return
      }

      this.$auth.removeToken()
      this.confirmationEmail = ''
      this.confirmationStatus = 'idle'
      this.registrationError = this.$t('keycloak.registrationGeneric')
      console.error('Inconsistent registration response', {
        configuredPath: this.effectiveRegistrationPath,
        responsePath: registrationPath,
        registrationStatus,
        hasJwt
      })
    },

    handleRegisterError(response) {
      const errors = response?.data?.errors
      const apiErrorCode = errors?.[0]?.code

      this.registrationError = undefined
      this.keyCloakError = ''

      if (apiErrorCode === 'REGISTER_EMAIL_UNCONFIRMED') {
        this.$auth.removeToken()
        this.confirmationEmail = this.registerEmail
        this.confirmationStatus = 'unconfirmed'
        this.localStep = 'confirmEmail'

        return
      }

      this.hasRegistrationBackendValidationErrors = true

      if (apiErrorCode === 'REGISTER_EMAIL_ALREADY_EXISTS') {
        const message = this.$t('loginForm.register.emailAlreadyExists')
        this.registerErrors.email = message
        this.keyCloakError = message

        return
      }

      if (apiErrorCode === 'REGISTER_IDENTITY_PROVIDER_ERROR') {
        this.registrationError = this.$t('loginForm.register.identityProviderError')

        return
      }

      if (response?.status === 422 && errors && !errors[0]) {
        _.each(errors, (error, field) => {
          this.registerErrors[field] = error
        })

        return
      }

      this.registrationError = this.$t('keycloak.registrationGeneric')
    },

    confirmEmail() {
      this.confirmationError = undefined
      this.resendConfirmationError = undefined
      this.confirmationStatus = 'loading'

      const token =
        this.$route?.query?.confirmation_token ||
        this.$route?.params?.confirmation_token ||
        this.confirmationToken
      if (!token) {
        this.confirmationStatus = 'invalid'
        this.confirmationError = this.$t('loginForm.confirmEmail.errors.invalidToken')
        return Promise.resolve()
      }

      return this.$http.put('confirmation', null, {
        params: { confirmation_token: token }
      }).then(() => {
        this.confirmationStatus = 'success'
        this.$auth?.removeToken?.()
      })
        .catch(error => {
          const code = error?.response?.data?.errors?.code || error?.response?.data?.errors?.confirmation || error?.response?.data?.errors?.token
          if (code && /expired/i.test(code)) {
            this.confirmationStatus = 'expired'
            this.confirmationError = this.$t('loginForm.confirmEmail.errors.expiredToken')
            return
          }

          if (code && /invalid|unconfirmed/i.test(code)) {
            this.confirmationStatus = 'invalid'
            this.confirmationError = this.$t('loginForm.confirmEmail.errors.invalidToken')
            return
          }

          this.confirmationStatus = 'error'
          this.confirmationError = this.$t('loginForm.confirmEmail.errors.generic')
        })
    },

    resendConfirmation() {
      if (this.resendConfirmationLoading) return Promise.resolve()

      // En estado error/expirado el input está ligado a confirmationEmail.
      if (!this.confirmationEmail) {
        this.resendConfirmationError = this.$t('loginForm.confirmEmail.errors.emailRequired')

        return Promise.resolve()
      }

      this.resendConfirmationLoading = true
      this.resendConfirmationError = undefined

      return this.$auth.resendConfirmation(this.confirmationEmail)
        .then(() => {
          this.$notifications.send({
            id: 'resend-confirmation-success',
            text: this.$t('toast_resend_success'),
            type: 'success',
            close: true
          })
        })
        .catch(() => {
          this.resendConfirmationError = this.$t('loginForm.confirmEmail.errors.resendGeneric')
        })
        .finally(() => {
          this.resendConfirmationLoading = false
        })
    },

    resetPasswordLink() {
      this.localStep = 'resetPassword'
    },

    createAccountLink() {
      this.localStep = 'register'
    },

    closePanel() {
      this.$emit('close-panel')
      this.$nextTick(() => {
        this.customaccessibilityTabs?.setup(this.$options.name)
      })
    },

    backToLogin() {
      this.localStep = 'login'
      this.confirmationStatus = 'idle'
      this.confirmationError = undefined
      this.confirmationEmail = ''
    },

    closePanelHome() {
      if (this.$route.name !== 'home' && !_.includes(this.$route.name, 'municipality')) {
        this.$router.push({ name: 'home' })
      }
      this.$emit('close-panel')
    },

    showConditions() {
      if (this.shouldShowConditions) {
        const routeData = 'https://autgc.org/aviso-legal/'
        window.open(routeData)
      } else {

        const routeName =
          this.$store.state.appCode === 'rd'
            ? 'privacidade-policy'
            : 'privacy-policy'

        const targetRoute = {
          name: routeName,
          params: {
            country: this.loadedCountry,
            municipality: this.loadedMunicipality
          }
        };

        this.$router.push(targetRoute);

        this.closePanel();
      }
    },

    gencaptcha() {
      let value = btoa(Math.random() * 1000000000);
      value = value.substr(0, 5 + Math.random() * 5);
      this.captchaValue = value;
    },

    setcaptcha() {
      let html = this.captchaValue.split("").map((char) => {
        const rotate = -20 + Math.trunc(Math.random() * 30);
        const font = Math.trunc(Math.random() * this.fonts.length);
        return `<span
          style="
          transform:rotate(${rotate}deg);
          font-family:${font[font]};
          "
          >${char} </span>`;
      }).join("");
      document.querySelector(".preview").innerHTML = html;
    },

    /**
    * Override the mixin handler
    * @param {Event} event 
    * @returns ?Event 
    */
    handleKeystroke(event) {
      // console.log('Keystroke', event.target)
      switch (event.code) {
        case 'Escape':
          this.closePanel()
          break
        case 'Enter':
        case 'Space':
          event.target.click()
          break
        default:
          return
      }
    },

    applyContrasteCss() {
      // console.log(`applying high contrast for component ${this.$options.name} (${this.contraste})`)

      if (this.contraste) {
        $(".preview ").css("cssText", "color: #1e1e1e !important")
      } else {
        $(".preview ").css("cssText", "color:  ")
      }
    },

    /* Valid if the available municipalities are equal to 1, to skip city selector */
    validateMuncipalityInRegister() {
      return this.availableMunicipalities.length !== 1
    }
  }
}
